<template>
	<div
		class="modal-window-wrapper"
		:class="{
			'modal-window-wrapper--visible': isVisible,
			'modal-window-wrapper--elevated': isElevated,
		}"
	>
		<div v-if="$slots.topBar" class="modal-window__top-bar">
			<slot name="topBar" />
		</div>
		<div
			class="modal-window"
			:class="{
				[`modal-window--${skin}`]: skin !== null,
				'modal-window--max-modal-height': hasMaxModalHeight,
			}"
			:data-event-type="eventType"
			:style="{
				maxHeight: Object.keys(routesWithFixedModalHeight).some(
					key => route.name === key,
				)
					? 'unset'
					: hasMaxModalHeight
						? routesWithMaxModalHeight[
								route.name as keyof typeof routesWithMaxModalHeight
							] || routesWithMaxModalHeight['any-70']
						: routesWithMaxModalHeight['any-50'],
				height: Object.keys(routesWithFixedModalHeight).some(
					key => route.name === key,
				)
					? routesWithFixedModalHeight[
							route.name as keyof typeof routesWithFixedModalHeight
						]
					: 'unset',
			}"
		>
			<div class="modal-window__header">
				<div
					class="header"
					:class="{ 'header--decorated': headerData?.level }"
				>
					<div v-if="headerData?.level" class="icon-tile-selector">
						<span class="icon-tile-selector__level">
							{{ headerData.level }}
						</span>
					</div>
					<div class="text">
						<span
							class="heading"
							:class="{ 'heading--corrected': subHeading }"
						>
							{{ heading }}
						</span>
						<span
							v-if="subHeading"
							class="sub-heading"
							v-html="subHeading"
						/>
					</div>
				</div>
				<div class="status">
					<span
						v-if="hasStatusText && statusData"
						class="status__value"
					>
						{{ `${statusData.current}/${statusData.total}` }}
					</span>

					<WindowCollapseToggleButton
						v-if="headerData?.context === UIContexts.SETTLEMENT"
						:hasNotification="
							headerData?.isAvailableUpgrade ?? false
						"
					/>
					<LayoutSwitcherButton
						v-if="headerData?.gridToggle && mainLevel >= 10"
					/>
					<AppIconButton
						v-if="headerData?.searchButton"
						:type="IconTypes.SEARCH"
						@click="openSearchModal"
					/>
					<AppIconButton
						:type="IconTypes.CLOSE"
						@click="closeModal"
					/>
				</div>
			</div>
			<div
				class="modal-window__divider"
				:class="{
					'modal-window__divider--premium':
						skin === ModalWindowSkinTypes.PREMIUM,
				}"
			>
				<div
					v-if="skin === ModalWindowSkinTypes.PREMIUM"
					class="base-horizontal-divider"
				/>
				<HorizontalDivider v-else />
			</div>
			<div v-if="hasActionBar" class="modal-window__action-bar">
				<div class="action-bar-left">
					<FilterDropdown
						:label="upgradeFilterValue"
						:items="[
							FilterItems.NEWEST,
							FilterItems.CHEAPEST,
							FilterItems.PREMIUM,
							// FilterItems.SPECIAL,
						]"
						@labelSelected="
							(labelSelected: FilterItems): void =>
								emit('labelSelected', labelSelected)
						"
					/>
				</div>
				<div class="action-bar-right">
					<AppIconButton
						:type="upgradeFilterVisibility"
						@click="handleVisibilityChange"
					/>
					<AppIconButton
						:type="IconTypes.INFO"
						:size="32"
						@click="emit('info')"
					/>
				</div>
			</div>
			<div
				class="modal-window__content"
				:class="{
					'modal-window__content--full-width': fullWidthContent,
				}"
			>
				<ScrollFade
					v-if="
						viewsRouteNamesWithFullHeightTabs.includes(
							route.name as ViewsRouteNamesWithFullHeightTabs,
						)
					"
				>
					<template #scrollContent>
						<slot name="content" />
					</template>
				</ScrollFade>
				<div
					v-else
					class="scroll-basic-container"
					:class="{
						'scroll-basic-container--paddingless': fullWidthContent,
					}"
				>
					<slot name="content" />
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import FilterDropdown from "@/components/FilterDropdown.vue";
import HorizontalDivider from "@/components/HorizontalDivider.vue";
import LayoutSwitcherButton from "@/components/LayoutSwitcherButton.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import SearchToolPopup from "@/components/SearchTool/SearchToolPopup.vue";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import router from "@/router";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore.ts";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUserStore from "@/store/useUserStore";
import type { EventCategory } from "@/types/ui";
import {
	IconTypes,
	ModalWindowSkinTypes,
	RouteNames,
	routesWithFixedModalHeight,
	routesWithMaxModalHeight,
	SkinTypes,
	UIContexts,
} from "@/types/ui";
import type { UpgradeStat } from "@/types/upgrades";
import { FilterItems } from "@/types/upgrades";
import { translate } from "@/utils/translate.ts";
import { storeToRefs } from "pinia";
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import WindowCollapseToggleButton from "./WindowCollapseToggleButton.vue";

interface Props {
	heading: string;
	subHeading?: string | null;
	hasStatusText?: boolean;
	hasActionBar?: boolean;
	statusData?: UpgradeStat;
	fullWidthContent?: boolean;
	skin?: ModalWindowSkinTypes | null;
	headerData?: {
		level?: number | null;
		gridToggle?: boolean | null;
		searchButton?: boolean | null;
		context?: UIContexts | null;
		isAvailableUpgrade?: boolean | null;
	};
	isElevated?: boolean;
	eventType?: EventCategory;
}

const {
	heading,
	subHeading = null,
	hasStatusText = false,
	hasActionBar = false,
	statusData = null,
	fullWidthContent = false,
	skin = null,
	headerData = null,
	isElevated = true,
} = defineProps<Props>();

const route = useRoute();

const emitter = markRaw(useEmitter());
const popupStore = usePopupStore();

const isVisible = ref<boolean>(false);

const { upgradeFilterVisibility } = storeToRefs(useUserStore());

const { upgradeFilterValue } = storeToRefs(useRuntimeStore());
const { mainLevel } = storeToRefs(useSettlementsStore());

// an array of screens that have full height tabs, since tabs are now internally faded
enum ViewsRouteNamesWithFullHeightTabs {
	Upgrades = RouteNames.UPGRADES,
	Shop = RouteNames.SHOP,
	// TODO 2. exception is also main settlement, screens RouteNames.TILE, but only for the center of each settlement
}

const viewsRouteNamesWithFullHeightTabs = [
	ViewsRouteNamesWithFullHeightTabs.Shop,
	ViewsRouteNamesWithFullHeightTabs.Upgrades,
];

const handleMapRedirect = (): void => {
	router.push({ name: RouteNames.LAYOUT_VIEW });
};

const closeModal = (): void => {
	// NOTE: deselect tile visually when redirecting
	emitter.emit(EmitterEvents.PhaserTileDeselect);

	handleMapRedirect();
};

const openSearchModal = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.SEARCH_TOOL,
		type: PopupType.INFO,
		heading: translate("ui.search_tool"),
		dropOnInterrupt: true,
		skin: SkinTypes.DARK,
		isPaddingless: true,
		isCentered: true,
		isPriority: true,
		height: 38,
		width: 23.875,
		component: markRaw(SearchToolPopup),
	});
};

const emit = defineEmits(["info", "changeVisibility", "labelSelected"]);

const handleVisibilityChange = (): void => {
	emit(
		"changeVisibility",
		upgradeFilterVisibility.value === IconTypes.HIDE
			? IconTypes.SHOW
			: IconTypes.HIDE,
	);
};

const isSettlement = computed((): boolean => {
	return route.params.isSettlement === "true";
});

const hasMaxModalHeight = computed((): boolean => {
	return (
		Object.keys(routesWithMaxModalHeight).some(
			(key: string): boolean => route.name === key,
		) || isSettlement.value
	);
});

onMounted((): void => {
	isVisible.value = true;
});

onBeforeUnmount((): void => {
	isVisible.value = false;
});
</script>

<style lang="scss" scoped>
$boxInsetWidth: 0.125rem;
$baseTexture: url($path-images + "backgrounds/textures/window-texture-bg.avif")
	repeat center;

.modal-window {
	display: flex;
	position: relative;
	flex-direction: column;
	box-shadow: inset 0 0 0 $boxInsetWidth rgba(255, 255, 255, 0.1);
	border-radius: 0.15rem 0.15rem 0 0;
	background: #27282b;
	background: $baseTexture;
	background-size: 90% auto;
	width: 100%;

	&::before {
		position: absolute;
		top: $boxInsetWidth;
		left: $boxInsetWidth;
		z-index: -1;
		border-radius: 0.15rem 0.15rem 0 0;
		width: calc(100% - $boxInsetWidth * 2);
		height: 100%;
		pointer-events: none;
		content: "";
	}

	// skins
	&--premium {
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			z-index: 0;
			background: rgba(49, 32, 55, 0.6);
			width: 100%;
			height: 100%;
			content: "";
		}
	}

	&--event {
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1;
			z-index: 1;
			mix-blend-mode: multiply;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}
		.modal-window__header {
			justify-content: space-between;
			border-bottom: 1px solid rgba(255, 215, 0, 0.1);
			background-color: rgba(20, 9, 11, 0.6);
			padding: 0.75rem 1rem;
			min-height: 4rem;
			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1;
				mix-blend-mode: multiply;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}
			.status {
				:deep(.app-icon-button) {
					box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.5);
					border: 0.125rem solid #57090e;
					background-color: #7d0b13;
				}
			}
		}
		&[data-event-type="christmas"] {
			background-image: linear-gradient(to bottom, #320607, #57090e);
			.modal-window__header {
				background-color: #340506;
			}
		}
		&[data-event-type="easter"] {
			background-image: linear-gradient(
				0deg,
				rgba(34, 42, 4, 1) 0%,
				rgba(61, 76, 3, 1) 100%
			);
			.modal-window__header {
				background-color: rgba(37, 45, 4, 1);
			}
		}
		&[data-event-type="fishing"] {
			background-image: linear-gradient(
				0deg,
				rgba(5, 48, 63, 1) 0%,
				rgba(9, 66, 87, 1) 100%
			);
			.modal-window__header {
				background-color: #052f3f;
			}
		}
		&[data-event-type="firefest"] {
			background-image: linear-gradient(
				0deg,
				rgba(67, 24, 13, 1) 0%,
				rgba(109, 33, 13, 1) 100%
			);
			.modal-window__header {
				background-color: #43180d;
			}
		}
		&[data-event-type="solarfair"] {
			background-image: linear-gradient(
				0deg,
				rgba(96, 57, 16, 1) 0%,
				rgba(137, 90, 36, 1) 100%
			);

			.modal-window__header {
				background-color: #603101;
			}
		}
		&[data-event-type="hunting"] {
			background-image: linear-gradient(
				0deg,
				rgba(12, 49, 5, 1) 0%,
				rgba(18, 63, 10, 1) 100%
			);

			.modal-window__header {
				background-color: #082a06;
			}
		}
		&[data-event-type="halloween"] {
			background-image: linear-gradient(
				0deg,
				rgba(27, 15, 56, 1) 0%,
				rgba(52, 39, 87, 1) 100%
			);

			.modal-window__header {
				background-color: #1f1439;
			}
		}
		&[data-event-type="pirates"] {
			background-image: linear-gradient(
				0deg,
				rgba(10, 35, 64, 1) 0%,
				rgba(19, 56, 98, 1) 100%
			);

			.modal-window__header {
				background-color: #0a2340;
			}
		}
	}

	&--max-modal-height {
		.scroll-fade-container,
		:deep(.scrollbar__wrapper) {
			display: flex;
			flex: 1 1 auto; // let it take up the remaining space in the content
			flex-direction: column;
			height: unset;
			// KEY: must not force 100% height, but allow shrinking
			min-height: 0;
			max-height: 100%;
			overflow-y: auto;
		}
	}

	&-wrapper {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		width: calc(100% - 0.5rem);
		max-width: 26.5rem;
		@include flex-col-center;
		bottom: calc(var(--footer-height) - $boxInsetWidth);
		z-index: 1;

		&--visible {
			animation: fade-bottom-to-base 0.3s forwards;
		}

		// e.g. for tile details elevation
		&--elevated {
			bottom: var(--safe-area-inset-bottom); // MOBILE
			z-index: 1;

			@include md {
				bottom: 1.25rem; // WEB
			}
		}
	}

	&__top-bar {
		position: absolute;
		top: -3.6rem; // 3.75rem - 0.15rem
		left: 1.25rem;
		box-shadow: inset 0 0 0 0.15rem rgba(255, 255, 255, 0.1);
		border-radius: 0.15rem 0.15rem 0 0;
		background-image: url($path-images + "backgrounds/textures/window-texture-bg.avif");
		background-size: 90% auto;
		background-size: 90% auto;
		background-repeat: repeat;
		padding: 0 0.8rem;
		min-width: 9rem;
		height: 3.75rem;
		overflow: hidden;

		&::before {
			position: absolute;
			top: 0.15rem;
			left: 0.15rem;
			mix-blend-mode: color;
			background-color: rgba(20, 22, 34, 1);
			width: calc(100% - 0.3rem);
			height: calc(100% - 0.3rem);
			content: "";
		}
	}

	&__header {
		position: relative;
		flex-shrink: 0;
		@include flex-row-stretch;
		gap: 0.5rem;
		z-index: 1;
		padding: 0.375rem;
		width: 100%;
		height: 3.25rem;

		.header {
			flex: 1 1 0;
			gap: 0.5rem;
			padding-left: 0.75rem;
			@include text-truncate;
			@include flex-row-center;

			&--decorated {
				padding-left: 0;
			}

			.icon-tile-selector {
				margin-bottom: -0.25rem;
				width: 2rem;
				height: 2rem;
				@include flex-col-center;

				&__level {
					margin-top: -0.375rem;
					color: #fff3dd;
					font-weight: 800;
					font-size: 1.25rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				}
			}

			.text {
				display: flex;
				flex: 1 1 0;
				flex-direction: column;
				gap: 0.25rem;
				@include text-truncate;

				.heading {
					color: #f4d278;
					font-weight: 800;
					font-size: 1.25rem;
					line-height: 1.25rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					text-transform: uppercase;
					@include text-truncate;

					&--corrected {
						margin-bottom: -0.25rem;
					}
				}

				.sub-heading {
					color: #fff3dd;
					font-size: 0.875rem;
					line-height: 1.25rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
					@include text-truncate;
				}
			}
		}

		.status {
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;
			gap: 0.625rem;

			&__value {
				color: #f4d278;
				font-weight: 800;
				font-size: 1.5rem;
			}
		}
	}

	&__divider {
		position: relative;
		flex-shrink: 0;
		z-index: 1;
		padding: 0 0.375rem;
		width: 100%;

		&--premium {
			padding: 0 $boxInsetWidth;
		}

		.base-horizontal-divider {
			@include background(
				url($path-backgrounds + "textures/top-menu-texture-golden-stroke.avif"),
				contain,
				center,
				repeat
			);
			padding: 0 $boxInsetWidth;
			width: 100%;
			height: 0.094rem;
		}
	}

	&__action-bar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.375rem;
		padding-top: 0.125rem;
		width: 100%;
	}

	.action-bar {
		&-right {
			@include flex-row-center;
			gap: 0.75rem;
			margin-right: 0.5rem;
		}
	}

	&__content {
		display: flex;
		flex: 1 1 auto;
		flex-direction: column;
		width: 100%;
		overflow-y: auto;
		color: #f2dcb5;
		font-size: 0.813rem;

		// NOTE: Due to the scrollbar, padding cannot be applied to the __content directly; it should be applied to the scroll-fade-container instead.
		.scroll-fade-container {
			:deep(.scrollbar__content) {
				padding: 0.5rem 0.5rem 1.125rem;
			}
		}

		.scroll-basic-container {
			padding: 0 0.5rem 0.625rem;
			height: 100%;

			&--paddingless {
				padding: 0;
			}
		}

		&--full-width {
			padding: 0.125rem 0 0;
			&::-webkit-scrollbar-thumb {
				background: #f4d278;
			}

			&::-webkit-scrollbar-track {
				box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
				-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
				background: #3c3d43;
			}

			&::-webkit-scrollbar {
				-webkit-appearance: none;
				width: 0.188rem;
				height: 0.188rem;
			}
		}
	}

	// TODO remove, not working on mobile
	:deep(.lok-scrollbar::-webkit-scrollbar-thumb) {
		background: #f4d278;
	}

	:deep(.lok-scrollbar::-webkit-scrollbar-track) {
		box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
		-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
		background: #3c3d43;
	}

	:deep(.lok-scrollbar::-webkit-scrollbar) {
		-webkit-appearance: none;
		width: 0.188rem;
		height: 0.188rem;
	}

	@keyframes fade-bottom-to-base {
		0% {
			transform: translateX(-50%) translateY(100%);
			opacity: 0;
		}
		100% {
			transform: translateX(-50%) translateY(0);
			opacity: 1;
		}
	}
}
</style>
